import React, { useState, useEffect } from 'react';
import { getAvatarUrl } from '../../../lib/avatars';
import { useGame } from '../../../contexts/GameContext';
import { CharacterStats } from '../../../types';
import './UserInfo.css';

const statNames: Record<keyof CharacterStats, string> = {
  strength: 'Сила',
  agility: 'Ловкость',
  intuition: 'Интуиция',
  endurance: 'Выносливость',
  intelligence: 'Интеллект',
};

const statIcons: Record<keyof CharacterStats, string> = {
  strength: '⚔️',
  agility: '🏃',
  intuition: '👁️',
  endurance: '💪',
  intelligence: '🧠',
};

const UserInfo: React.FC = () => {
  const { state, spendStatPoints } = useGame();
  const { character, characterLoading, locations } = state;

  const [tempStats, setTempStats] = useState<Partial<CharacterStats>>({});
  const [pointsToSpend, setPointsToSpend] = useState(0);

  useEffect(() => {
    if (character) {
      setPointsToSpend(character.unspent_stat_points || 0);
      setTempStats({});
    }
  }, [character]);

  const handleStatChange = (stat: keyof CharacterStats, amount: number) => {
    if (amount > 0 && pointsToSpend > 0) {
      setTempStats(prev => ({ ...prev, [stat]: (prev[stat] || 0) + 1 }));
      setPointsToSpend(prev => prev - 1);
    } else if (amount < 0 && tempStats[stat] && tempStats[stat]! > 0) {
      setTempStats(prev => ({ ...prev, [stat]: (prev[stat] || 0) - 1 }));
      setPointsToSpend(prev => prev + 1);
    }
  };

  const handleSaveChanges = () => {
    spendStatPoints(tempStats);
    setTempStats({});
  };

  const handleCancelChanges = () => {
    setPointsToSpend(character?.unspent_stat_points || 0);
    setTempStats({});
  };

  if (!character || characterLoading) {
    return <div className="user-info">Загрузка...</div>;
  }

  const characterClass = character.character_class;
  const expForCurrentLevel = character.exp_for_current_level || 0;
  const expForNextLevel = character.exp_for_next_level || expForCurrentLevel;
  
  // Логирование для отладки
  console.log('Experience data:', {
    level: character.level,
    experience: character.experience,
    exp_for_current_level: character.exp_for_current_level,
    exp_for_next_level: character.exp_for_next_level,
    expForCurrentLevel,
    expForNextLevel
  });
  
  // Улучшенный расчет прогресса опыта с обработкой edge cases
  let expProgress = 0;
  if (character.experience >= expForNextLevel) {
    // Если опыт больше или равен следующему уровню, показываем 100%
    expProgress = 100;
  } else if (expForNextLevel > expForCurrentLevel) {
    const expInCurrentLevel = Math.max(0, character.experience - expForCurrentLevel);
    const expNeededForLevel = expForNextLevel - expForCurrentLevel;
    expProgress = Math.min(100, Math.max(0, (expInCurrentLevel / expNeededForLevel) * 100));
  }
  
  console.log('Calculated progress:', expProgress);

  return (
    <div className="user-info">
      <div className="user-info-header">
        <div className="user-avatar">
          {character.avatar ? (
            <img 
              src={getAvatarUrl(character.avatar)} 
              alt={character.nickname}
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzMzMyIvPgogIDx0ZXh0IHg9IjUwIiB5PSI1MCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM5OTkiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCI+Tm8gQXZhdGFyPC90ZXh0Pgo8L3N2Zz4=';
              }}
            />
          ) : (
            <div className="no-avatar">?</div>
          )}
        </div>
        <div className="user-main-info">
          <h3 className="user-nickname">
            {character.nickname} <span className="user-level-inline">[{character.level}]</span>
          </h3>
          <p className="user-class">{characterClass?.name || 'Неизвестный класс'}</p>
          <div className="user-location">
            <span className="location-icon">📍</span>
            <span>
              {(() => {
                const locationId = character.current_location_id;
                if (!locationId) {
                  return 'Неизвестная локация';
                }
                
                if (locationId.type === 'traveling') {
                  const toLocation = locations.find(loc => loc.id === locationId.to_location_id);
                  return `В пути → ${toLocation?.name || 'Неизвестная локация'}`;
                }
                
                if (locationId.type === 'in_location') {
                  const currentLocation = locations.find(loc => loc.id === locationId.location_id);
                  return currentLocation?.name || 'Загрузка...';
                }
                
                return 'Неизвестная локация';
              })()}
            </span>
          </div>
        </div>
      </div>

      <div className="user-experience" key={`exp-${character.level}-${character.experience}`}>
        <div className="exp-label">
          <span>Опыт</span>
          <span>{character.experience.toLocaleString()} / {expForNextLevel.toLocaleString()}</span>
        </div>
        <div className="exp-bar">
          <div 
            className="exp-progress" 
            style={{ width: `${expProgress}%` }}
          />
        </div>
      </div>

      <div className="user-stats">
        <div className="stats-header">
          <h4>Характеристики</h4>
          {(character.unspent_stat_points || 0) > 0 && <span className="points-to-spend">Очки: {pointsToSpend}</span>}
        </div>
        <div className="stats-list">
          {(Object.keys(character.stats) as Array<keyof CharacterStats>).map(statKey => (
            <div className="stat-item" key={statKey}>
              <span className="stat-icon">{statIcons[statKey]}</span>
              <span className="stat-name">{statNames[statKey]}</span>
              <span className="stat-value">
                {character.stats[statKey] + (tempStats[statKey] || 0)}
                {(tempStats[statKey] || 0) > 0 && <span className="stat-temp-bonus"> +{tempStats[statKey]}</span>}
              </span>
              {(character.unspent_stat_points || 0) > 0 && (
                <div className="stat-controls">
                  <button 
                    onClick={() => handleStatChange(statKey, 1)} 
                    disabled={pointsToSpend === 0}
                    className="stat-btn"
                  >+</button>
                  <button 
                    onClick={() => handleStatChange(statKey, -1)} 
                    disabled={!tempStats[statKey] || tempStats[statKey] === 0}
                    className="stat-btn"
                  >-</button>
                </div>
              )}
            </div>
          ))}
        </div>
        {Object.values(tempStats).some(v => v > 0) && (
          <div className="stats-actions">
            <button className="btn btn-primary" onClick={handleSaveChanges}>Сохранить</button>
            <button className="btn btn-secondary" onClick={handleCancelChanges}>Отмена</button>
          </div>
        )}
      </div>

      <div className="user-bars">
        <div className="bar-container">
          <div className="bar-label">
            <span>HP</span>
            <span>{character.derivedStats?.maxHP || 100}/{character.derivedStats?.maxHP || 100}</span>
          </div>
          <div className="bar hp-bar">
            <div className="bar-fill hp-fill" style={{ width: '100%' }} />
          </div>
        </div>
        <div className="bar-container">
          <div className="bar-label">
            <span>MP</span>
            <span>{character.derivedStats?.maxMana || 50}/{character.derivedStats?.maxMana || 50}</span>
          </div>
          <div className="bar mp-bar">
            <div className="bar-fill mp-fill" style={{ width: '100%' }} />
          </div>
        </div>
      </div>

      {character.derivedStats && (
        <div className="user-derived-stats">
          <h4>Боевые параметры</h4>
          <div className="derived-stats-list">
            <div className="stat-item">
              <span className="stat-icon">🛡️</span>
              <span className="stat-name">Броня</span>
              <span className="stat-value">{character.derivedStats.armor}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">⚡</span>
              <span className="stat-name">Уклонение</span>
              <span className="stat-value">{character.derivedStats.dodgeChance}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">💥</span>
              <span className="stat-name">Крит. шанс</span>
              <span className="stat-value">{character.derivedStats.critChance}%</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">🗡️</span>
              <span className="stat-name">Базовый урон</span>
              <span className="stat-value">{character.derivedStats.baseDamage}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInfo;
